/**
 * @file CertificateFileField.tsx
 * @description Блок «Файл» в окне сертификата производителя (phase19.16,
 * партия «Юридические документы», шаг В; SB141; uiux-legal-steps-b-c §6.3):
 * без файла — «Выбрать файл» 56 px и «PDF, JPG или PNG, до 20 МБ»; с файлом —
 * вид, имя, размер, дата и [Открыть] [Заменить] [Убрать] по 48 px; во время
 * загрузки — «Загружаем файл — 64%» с полосой; ошибка — у блока.
 *
 * Только показ: выбор, загрузку и удаление ведёт `useCertificateFile`,
 * подтверждение «Убрать» — окно сертификата.
 */

import { formatLegalFileSize } from "@chaiberi/shared";
import { FileText, Upload } from "lucide-react";
import { useRef } from "react";

import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { NoticeStrip } from "../../ui/NoticeStrip";
import type { ProducerCertificateFile } from "../../../lib/api-catalog";
import { CERT_FILE_ACCEPT } from "../../../lib/certificate-status";
import { formatShopDate } from "../../../lib/shop-time";

interface Props {
  /** Файл на сервере (null — нет); у нового сертификата — null. */
  file: ProducerCertificateFile | null;
  /** Выбран для нового сертификата, уйдёт по «Сохранить». */
  pending: File | null;
  progress: number | null;
  error: string | null;
  /** Сертификат уже сохранён: замена грузится сразу. */
  saved: boolean;
  disabled?: boolean;
  /** Без права каталога: только «Открыть», без выбора, замены и удаления. */
  readOnly?: boolean;
  onPick: (file: File) => void;
  onOpen: () => void;
  onRemove: () => void;
}

const kindOf = (mime: string): string => (mime === "application/pdf" ? "PDF" : mime === "image/png" ? "PNG" : "JPG");

export function CertificateFileField({ file, pending, progress, error, saved, disabled, readOnly = false, onPick, onOpen, onRemove }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const shown = pending ? { name: pending.name, sizeBytes: pending.size, mime: pending.type, uploadedAt: null } : file;
  const busy = disabled || progress !== null;
  const choose = () => input.current?.click();

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="ct-file">Файл</Label>
      <input
        ref={input}
        id="ct-file"
        type="file"
        accept={CERT_FILE_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        disabled={busy || readOnly}
        onChange={(e) => {
          const next = e.target.files?.[0];
          e.target.value = "";
          if (next) onPick(next);
        }}
      />
      <div className="flex flex-col gap-2 rounded-xl p-3 [box-shadow:inset_0_0_0_1px_var(--border)]">
        {progress !== null ? (
          <div className="flex flex-col gap-1" role="status">
            <span className="type-body text-on-surface">Загружаем файл — {progress}%</span>
            <div className="h-1.5 overflow-hidden rounded-full bg-surface-low">
              <div className="h-full bg-primary transition-[width]" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : shown ? (
          <>
            <div className="flex items-start gap-2">
              <FileText aria-hidden className="mt-0.5 size-4 shrink-0 text-on-surface-variant" />
              <div className="flex min-w-0 flex-col">
                <span className="type-body break-all text-on-surface">
                  {kindOf(shown.mime)} · {shown.name} · {formatLegalFileSize(shown.sizeBytes)}
                </span>
                {pending ? <span className="type-caption text-on-surface-variant">загрузится после «Сохранить»</span> : null}
                {!pending && shown.uploadedAt ? <span className="type-caption text-on-surface-variant">загружен {formatShopDate(shown.uploadedAt)}</span> : null}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {pending ? null : (
                <Button type="button" variant="outline" onClick={onOpen} disabled={busy}>
                  Открыть
                </Button>
              )}
              {readOnly ? null : (
                <>
                  <Button type="button" variant="outline" onClick={choose} disabled={busy}>
                    Заменить
                  </Button>
                  <Button type="button" variant="ghost" className="text-destructive" onClick={onRemove} disabled={busy}>
                    Убрать
                  </Button>
                </>
              )}
            </div>
            {saved && !pending && !readOnly ? <span className="type-caption text-on-surface-variant">Новый файл сразу заменит прежний на сайте.</span> : null}
          </>
        ) : readOnly ? (
          <span className="type-body text-on-surface-variant">Файла нет</span>
        ) : (
          <>
            <Button type="button" variant="outline" className="min-h-14 w-full" onClick={choose} disabled={busy}>
              <Upload aria-hidden className="size-4" />
              Выбрать файл
            </Button>
            <span className="type-caption text-on-surface-variant">PDF, JPG или PNG, до 20 МБ</span>
          </>
        )}
      </div>
      {error ? <NoticeStrip tone="error">{error}</NoticeStrip> : null}
    </div>
  );
}
